import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, BarChart3, TrendingUp, Eye, MousePointer, DollarSign, Plug } from "lucide-react";
import api from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/Page";

const kpiConfig = [
  { key: "draft", label: "Draft", color: "border-l-gray-400" },
  { key: "awaiting", label: "Awaiting", color: "border-l-yellow-500" },
  { key: "ready", label: "Ready", color: "border-l-blue-500" },
  { key: "live", label: "Live", color: "border-l-emerald-500" },
  { key: "paused", label: "Paused", color: "border-l-orange-500" },
];

const automationKeys = [
  { key: "auto_pause", label: "Auto-pause underperforming ads" },
  { key: "auto_scale", label: "Auto-scale top performers" },
  { key: "budget_recs", label: "Daily budget recommendations" },
  { key: "audience_expand", label: "Audience expansion suggestions" },
];

const PaidAds = () => {
  const [data, setData] = useState({ kpi: {}, campaigns: [], meta_connected: false, automations: {} });
  const [tab, setTab] = useState("Campaigns");
  const [showSetup, setShowSetup] = useState(false);
  const [setupForm, setSetupForm] = useState({ ad_account_id: "", default_radius: "10" });
  const [perf, setPerf] = useState(null);

  const load = () => api.get("/ads/dashboard").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tab === "Performance" && !perf) {
      api.get("/ads/performance").then(({ data }) => setPerf(data)).catch(() => {});
    }
  }, [tab, perf]);

  const connect = async () => {
    try {
      await api.post("/ads/connect-meta", setupForm);
      toast.success("Meta Ad Account connected");
      setShowSetup(false);
      load();
    } catch { toast.error("Connect failed"); }
  };

  const disconnect = async () => {
    try {
      await api.post("/ads/disconnect-meta");
      toast.success("Meta Ad Account disconnected");
      load();
    } catch { toast.error("Disconnect failed"); }
  };

  const toggleAutomation = async (key) => {
    const next = { ...data.automations, [key]: !data.automations[key] };
    setData({ ...data, automations: next });
    try { await api.put("/ads/automations", next); }
    catch { toast.error("Save failed"); load(); }
  };

  const maxSpend = perf ? Math.max(...perf.series.map((s) => s.spend), 1) : 1;

  return (
    <PageContainer>
      <PageHeader title="Paid Ads" subtitle="Plan and launch campaigns with AI." testId="ads-header"
        actions={data.meta_connected ? (
          <div className="flex items-center gap-2">
            <span className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200 flex items-center gap-1.5" data-testid="ads-meta-connected">
              <Plug size={12} /> Meta connected · {data.ad_account_id}
            </span>
            <button onClick={disconnect} className="op-btn-secondary text-xs" data-testid="ads-disconnect">Disconnect</button>
          </div>
        ) : (
          <button onClick={() => setShowSetup(true)} className="op-btn-primary" data-testid="ads-connect-meta">Connect Meta →</button>
        )} />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        {kpiConfig.map((k) => (
          <div key={k.key} className={`op-card p-5 border-l-4 ${k.color}`} data-testid={`ads-kpi-${k.key}`}>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{k.label}</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{data.kpi[k.key] || 0}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-2 border-b border-gray-200 mb-6">
        {["Campaigns", "Performance", "Settings"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === t ? "border-[#7C3AED] text-[#7C3AED]" : "border-transparent text-gray-600"}`} data-testid={`ads-tab-${t.toLowerCase()}`}>{t}</button>
        ))}
      </div>

      {tab === "Campaigns" && (
        <div className="op-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">
              <tr><th className="px-5 py-3">Name</th><th className="px-5 py-3">Objective</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Spend</th><th className="px-5 py-3">Impressions</th><th className="px-5 py-3">Clicks</th><th className="px-5 py-3">CTR</th></tr>
            </thead>
            <tbody>
              {data.campaigns.map((c) => (
                <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-5 py-3 font-medium">{c.name}</td>
                  <td className="px-5 py-3 text-gray-500">{c.objective}</td>
                  <td className="px-5 py-3 capitalize"><span className="px-2 py-0.5 bg-purple-100 text-[#7C3AED] rounded-full text-xs font-medium">{c.status}</span></td>
                  <td className="px-5 py-3">${c.spend.toFixed(2)}</td>
                  <td className="px-5 py-3">{c.impressions.toLocaleString()}</td>
                  <td className="px-5 py-3">{c.clicks.toLocaleString()}</td>
                  <td className="px-5 py-3">{c.ctr}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "Performance" && (
        <div className="space-y-6">
          {perf ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: DollarSign, label: "Total Spend", val: `$${perf.totals.spend}` },
                  { icon: Eye, label: "Impressions", val: perf.totals.impressions.toLocaleString() },
                  { icon: MousePointer, label: "Clicks", val: perf.totals.clicks.toLocaleString() },
                  { icon: TrendingUp, label: "CTR", val: `${perf.totals.ctr}%` },
                ].map((s) => (
                  <div key={s.label} className="op-card p-5">
                    <div className="flex items-center gap-2 text-gray-500 mb-2"><s.icon size={14} /><span className="text-xs uppercase font-semibold tracking-wider">{s.label}</span></div>
                    <p className="text-2xl font-bold">{s.val}</p>
                  </div>
                ))}
              </div>
              <div className="op-card p-6">
                <div className="flex items-center gap-2 mb-4"><BarChart3 size={16} className="text-[#7C3AED]" /><h3 className="font-semibold">Daily spend (last 14 days)</h3></div>
                <div className="flex items-end gap-1.5 h-40" data-testid="ads-perf-chart">
                  {perf.series.map((s) => (
                    <div key={s.date} className="flex-1 flex flex-col items-center gap-1 group relative h-full">
                      <div className="w-full flex-1 flex items-end">
                        <div className="w-full bg-gradient-to-t from-[#7C3AED] to-[#A78BFA] rounded-t transition-all group-hover:opacity-80" style={{ height: `${Math.max((s.spend / maxSpend) * 100, 4)}%` }} />
                      </div>
                      <span className="text-[10px] text-gray-400">{s.date.slice(-2)}</span>
                      <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">${s.spend} · {s.clicks} clicks</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="op-card p-8 text-center text-gray-500">Loading performance data…</div>
          )}
        </div>
      )}

      {tab === "Settings" && (
        <div className="op-card p-6 max-w-xl">
          <h3 className="font-semibold text-gray-900 mb-4">AI Automation</h3>
          {automationKeys.map((a) => {
            const on = !!data.automations[a.key];
            return (
              <div key={a.key} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                <p className="text-sm text-gray-700">{a.label}</p>
                <button onClick={() => toggleAutomation(a.key)} className={`w-11 h-6 rounded-full p-0.5 transition-colors ${on ? "bg-[#7C3AED]" : "bg-gray-300"}`} data-testid={`ads-toggle-${a.key}`}>
                  <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : ""}`} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {showSetup && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="meta-setup-modal" onClick={() => setShowSetup(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Connect Meta Ad Account</h2>
              <button onClick={() => setShowSetup(false)}><X size={18} className="text-gray-400" /></button>
            </div>
            <p className="text-sm text-gray-500 mb-5">Enter your Meta Business credentials to start launching campaigns. (MOCKED in v1 — no real API call.)</p>
            <div className="space-y-3 mb-5">
              <div>
                <label className="text-xs font-semibold text-gray-700">Ad Account ID</label>
                <input value={setupForm.ad_account_id} onChange={(e) => setSetupForm({ ...setupForm, ad_account_id: e.target.value })} className="op-input mt-1" placeholder="act_1234567890" data-testid="meta-acct-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">Default targeting radius (mi)</label>
                <input value={setupForm.default_radius} onChange={(e) => setSetupForm({ ...setupForm, default_radius: e.target.value })} className="op-input mt-1" placeholder="10" data-testid="meta-radius-input" />
              </div>
            </div>
            <ul className="space-y-1.5 mb-5">
              {["Sync existing campaigns", "Auto-import audiences", "Track conversions"].map((f) => (
                <li key={f} className="flex items-center gap-2 text-xs text-gray-600"><Check size={12} className="text-emerald-500" /> {f}</li>
              ))}
            </ul>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowSetup(false)} className="op-btn-secondary">Cancel</button>
              <button onClick={connect} disabled={!setupForm.ad_account_id} className="op-btn-primary" data-testid="meta-confirm-connect">Connect →</button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};
export default PaidAds;
